import { z } from 'zod/v4'

import { jsonDocument } from '../../contract/index.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Plantillas blockchain» › the list (templates with their counts, the platform networks and the blockchain
 * catalogue) and the editor of one template. The list does NOT carry the hierarchy (the editor does) nor who
 * created or modified a template; a row says how much a delete would remove (`contents`) instead.
 */

/** The list is read whole up to here; beyond it the page says how many of the total it shows. */
export const ADMIN_BLOCKCHAIN_TEMPLATES_CAP = 200

/** The stats of one protocol: the fields, so the contract (strict) and the route (loose) declare them ONCE. */
export const adminBlockchainTemplateProtocolStatsShape = {
  protocolName: z.string(),
  contractsCount: z.number(),
  /** Every contract rule of the protocol, active or not: what the admin wrote. */
  rulesCount: z.number(),
  /**
   * The contract rules the engine can really apply (`isActive`): the number the publish, badge and delete guards
   * look at. Optional because a summary saved before it existed has none: absent is «I do not know», never zero.
   */
  activeRulesCount: z.number().optional(),
}

/**
 * The stats of a template as a function of how its protocol entries are declared: the screen contract passes a
 * `z.strictObject`, the route of the template (`swagger-schemas.ts`, which serialises with `z.object`) a loose one.
 * The fields are the same.
 */
export const adminBlockchainTemplateStatsShape = <P extends z.ZodType>(protocolStats: P) => ({
  protocolsCount: z.number(),
  contractsCount: z.number(),
  rulesCount: z.number(),
  protocolsStats: z.array(protocolStats),
  lastCalculated: z.string(),
})

export const adminBlockchainTemplateStatsSchema = z.strictObject(
  adminBlockchainTemplateStatsShape(z.strictObject(adminBlockchainTemplateProtocolStatsShape)),
)

/** What a delete of the template removes, counted from its hierarchy. */
export const adminBlockchainTemplateContentsSchema = z.strictObject({
  protocols: z.number(),
  contracts: z.number(),
  rules: z.number(),
  globalRules: z.number(),
})

export const adminBlockchainTemplateRowSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  blockchain: z.string(),
  chainId: z.number().nullable(),
  networkType: z.string(),
  isActive: z.boolean(),
  isProductionReady: z.boolean(),
  isWalletImportSupported: z.boolean().nullable(),
  stats: adminBlockchainTemplateStatsSchema.nullable(),
  contents: adminBlockchainTemplateContentsSchema,
})

export const adminTemplateNetworkSchema = z.strictObject({
  chainId: z.number(),
  slug: z.string(),
  displayName: z.string(),
  enabled: z.boolean(),
  isEvm: z.boolean(),
  brandColor: z.string().nullable(),
  rpcEndpointCount: z.number(),
  enabledRpcEndpointCount: z.number(),
})

export const adminBlockchainCatalogOptionSchema = z.strictObject({
  slug: z.string(),
  displayName: z.string(),
  chainId: z.number().nullable(),
  isEvm: z.boolean(),
  brandColor: z.string().nullable(),
  nativeAsset: z.string().nullable(),
})

/**
 * The document the editor edits and sends back WHOLE in the `PUT`. It is a free document on purpose: the route of the
 * template is loose at every level (`BlockchainTemplateHierarchySchema`) because the shape of protocols and contracts
 * varies between generations of templates, and a strict top level here would drop in silence the keys the editor
 * does not know when it saves. The server validates it on save; the view checks it is a template document before
 * serving it (`countHierarchyContents`).
 */
export const adminBlockchainTemplateHierarchySchema = jsonDocument({
  reason:
    'the hierarchy of a template, edited whole by the editor and sent back in the PUT; its shape varies between generations of templates and unknown keys must survive a save',
})

export const adminBlockchainTemplateDetailSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  version: z.string(),
  blockchain: z.string(),
  chainId: z.number().nullable(),
  networkType: z.string(),
  hierarchy: adminBlockchainTemplateHierarchySchema,
  isActive: z.boolean(),
  isProductionReady: z.boolean(),
  isWalletImportSupported: z.boolean().nullable(),
  stats: adminBlockchainTemplateStatsSchema.nullable(),
  updatedAt: z.string(),
})

export const adminBlockchainTemplatesScreen = defineScreen({
  id: 'admin.blockchainTemplates',
  path: '/api/screens/admin/blockchain-templates',
  access: 'super_admin',
  query: z.strictObject({}),
  response: z.strictObject({
    templates: screenSection(
      z.strictObject({ rows: z.array(adminBlockchainTemplateRowSchema), total: z.number() }),
    ),
    networks: screenSection(z.array(adminTemplateNetworkSchema)),
    blockchains: screenSection(z.array(adminBlockchainCatalogOptionSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.blockchainTemplates'],
  budget: { requests: 1, statements: 10, bytes: 60_000, p95Ms: 300 },
  summary:
    'Blockchain templates: every template with its counts, the platform networks and the blockchain catalogue',
})

export const adminBlockchainTemplateDetailScreen = defineScreen({
  id: 'admin.blockchainTemplates.detail',
  path: '/api/screens/admin/blockchain-templates/detail',
  access: 'super_admin',
  query: z.strictObject({ id: z.guid() }),
  response: z.strictObject({
    /** The subject: an unknown id is the 404 `BLOCKCHAIN_TEMPLATE_NOT_FOUND` of the whole screen. */
    template: adminBlockchainTemplateDetailSchema,
    unknownContracts: screenSection(z.strictObject({ total: z.number() })),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.blockchainTemplates'],
  budget: { requests: 1, statements: 6, bytes: 250_000, p95Ms: 300 },
  summary:
    'One blockchain template as the editor edits it, and how many unknown contracts its network has',
})

export type AdminBlockchainTemplateStats = z.infer<typeof adminBlockchainTemplateStatsSchema>
export type AdminBlockchainTemplateContents = z.infer<typeof adminBlockchainTemplateContentsSchema>
export type AdminBlockchainTemplateRow = z.infer<typeof adminBlockchainTemplateRowSchema>
export type AdminTemplateNetwork = z.infer<typeof adminTemplateNetworkSchema>
export type AdminBlockchainCatalogOption = z.infer<typeof adminBlockchainCatalogOptionSchema>
export type AdminBlockchainTemplateDetail = z.infer<typeof adminBlockchainTemplateDetailSchema>
export type AdminBlockchainTemplatesData = ScreenData<typeof adminBlockchainTemplatesScreen>
export type AdminBlockchainTemplateDetailData = ScreenData<
  typeof adminBlockchainTemplateDetailScreen
>
export type AdminBlockchainTemplateDetailQuery = ScreenQuery<
  typeof adminBlockchainTemplateDetailScreen
>
